import {
  Calendar,
  Check,
  CheckCheck,
  ChevronDown,
  Clock,
  Download,
  Search,
  TrendingUp,
  Users,
  X,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { useGetWorkingHoursConfigQuery } from "../../store/apis/organization.api";
import {
  useGetTimesheetsQuery,
  useActOnTimesheetMutation,
} from "../../store/apis/timesheet.api";
import type { ApiError } from "../../store/utils/apiError";

import { TimesheetEntry } from "../../types/timesheet";
import { TimesheetQueryStatus } from "../../types/timesheet.api.types";
import { DEBOUNCE_DELAYS, useDebounce } from "../../utils/debounce";
import {
  formatHours,
  formatWeekRangeLabel,
  getRecentWeekOptions,
  getTimesheetStatusPill,
  getUtilColor,
  getWeekRange,
  toEntryDateKey,
  toWeekDateKey,
} from "../../utils/timesheetWeek";
import {
  PageHeader,
  Pagination,
  StatsCards,
  TabSelector,
} from "../common";
import TeamTimesheetsTable from "./TeamTimesheetsTable";
import { useHasPermission } from "../../store/hooks/useRbac";
import { PERMISSIONS } from "../../utils/rbac/permissions";
import { useAuth } from "../../store/hooks/useAuth";
import {
  RoleTypeEnum,
  isApprovedWorkflowStatus,
  isPendingWorkflowStatus,
  isRejectedWorkflowStatus,
  TIMESHEET_STATUS,
  VIEW_MODE,
  ViewMode,
} from "../../utils/constants";
import { TimesheetManagementSkeleton } from "./Skeleton";
import RejectionModal from "../leave/modals/RejectionModal";

dayjs.extend(utc);
dayjs.extend(timezone);

const WEEK_STATS_LIMIT = 100;

const TimesheetManagement: React.FC = () => {
  const { user } = useAuth();

  const isAdmin =
    user?.role?.toLowerCase() === RoleTypeEnum.ADMIN?.toLowerCase() ||
    user?.role?.toLowerCase() === RoleTypeEnum.SUPER_ADMIN?.toLowerCase();

  const canActOnApprovals = useHasPermission(
    PERMISSIONS.TIMESHEET_MANAGEMENT_MANAGE,
  );

  const { data: workingHoursConfig } = useGetWorkingHoursConfigQuery(
    user?.organizationId || "",
    { skip: !user?.organizationId },
  );
  const fullDayHours = Number(workingHoursConfig?.fullDayHours) || 7;

  /* =====================
   WEEK SELECTOR
===================== */

  const weekOptions = useMemo(() => getRecentWeekOptions(12), []);
  const [weekOffset, setWeekOffset] = useState(0);
  const range = useMemo(() => getWeekRange(weekOffset), [weekOffset]);
  const startKey = toWeekDateKey(range.start);
  const endKey = toWeekDateKey(range.end);
  const weekLabel = formatWeekRangeLabel(range);

  const handleWeekChange = (offset: number) => {
    setWeekOffset(offset);
    setPendingPage(1);
    setHistoryPage(1);
  };

  /* =====================
   VIEW MODE (shared for both accordions)
===================== */

  const [viewMode, setViewMode] = useState<ViewMode>(VIEW_MODE.ALL);
  const reporteesOnly = !isAdmin || viewMode === VIEW_MODE.REPORTEES;

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    setPendingPage(1);
    setHistoryPage(1);
  };

  /* =====================
   ACCORDION + PAGINATION
===================== */

  const [activeTab, setActiveTab] = useState<"pending" | "history">("pending");

  const [pendingPage, setPendingPage] = useState(1);
  const [pendingLimit, setPendingLimit] = useState(10);
  const [pendingSearchInput, setPendingSearchInput] = useState("");
  const debouncedPendingSearch = useDebounce(
    pendingSearchInput,
    DEBOUNCE_DELAYS.SEARCH,
  );

  const [historyPage, setHistoryPage] = useState(1);
  const [historyLimit, setHistoryLimit] = useState(10);
  const [historySearchInput, setHistorySearchInput] = useState("");
  const debouncedHistorySearch = useDebounce(
    historySearchInput,
    DEBOUNCE_DELAYS.SEARCH,
  );

  // Rejection modal state
  const [selectedTimesheet, setSelectedTimesheet] = useState<string | null>(
    null,
  );
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);

  /* =====================
   QUERIES
===================== */

  const {
    data: pendingRes,
    isLoading: pendingLoading,
    isFetching: pendingFetching,
  } = useGetTimesheetsQuery({
    search: debouncedPendingSearch || undefined,
    page: pendingPage,
    limit: pendingLimit,
    status: TimesheetQueryStatus.PENDING,
    startDate: startKey,
    endDate: endKey,
    reporteesOnly,
  });

  const {
    data: historyRes,
    isLoading: historyLoading,
    isFetching: historyFetching,
  } = useGetTimesheetsQuery({
    search: debouncedHistorySearch || undefined,
    page: historyPage,
    limit: historyLimit,
    status: TimesheetQueryStatus.HISTORY,
    startDate: startKey,
    endDate: endKey,
    reporteesOnly,
  });

  // Week-scoped all-status data for the summary cards + export
  const { data: weekStatsRes, isFetching: weekStatsFetching } =
    useGetTimesheetsQuery({
      page: 1,
      limit: WEEK_STATS_LIMIT,
      startDate: startKey,
      endDate: endKey,
      reporteesOnly,
    });
  const weekStats: TimesheetEntry[] = weekStatsRes?.data ?? [];

  const pendingTimesheets = pendingRes?.data ?? [];
  const pendingTotal = pendingRes?.total ?? 0;
  const historyTimesheets = historyRes?.data ?? [];
  const historyTotal = historyRes?.total ?? 0;

  const [
    actOnTimesheet,
    {
      isLoading: actionLoading,
      isSuccess: actionSuccess,
      isError: actionError,
      error: actionErrorData,
    },
  ] = useActOnTimesheetMutation();

  const currentUserId = user?.id;
  useEffect(() => {
    if (actionSuccess) {
      toast.success("Timesheet updated successfully");
    }
    if (actionError) {
      const err = actionErrorData as ApiError & {
        data?: { error?: string | string[] };
      };
      const rawMessage =
        err?.data?.message ||
        err?.data?.error ||
        err?.message ||
        "Something went wrong";
      const message = Array.isArray(rawMessage)
        ? rawMessage.join(", ")
        : rawMessage;
      toast.error(message);
    }
  }, [actionSuccess, actionError, actionErrorData]);

  const canActOnTimesheet = useCallback(
    (timesheet: TimesheetEntry): boolean => {
      if (!canActOnApprovals) return false;
      if (timesheet.status === TIMESHEET_STATUS.DRAFT) return false;
      if (isAdmin) return true;
      if (!timesheet.currentActorIds || !currentUserId) return false;
      return timesheet.currentActorIds.includes(currentUserId);
    },
    [canActOnApprovals, isAdmin, currentUserId],
  );

  /* =====================
   SUMMARY CARDS
===================== */

  const weekSummary = useMemo(() => {
    const teamMembers = new Set(weekStats.map((e) => e.userId)).size;
    const totalLogged = weekStats.reduce(
      (sum, e) => sum + (e.totalHours || 0),
      0,
    );
    const pendingCount = weekStats.filter((e) =>
      isPendingWorkflowStatus(e.status),
    ).length;

    let utilSum = 0;
    let utilCount = 0;
    for (const e of weekStats) {
      if (fullDayHours > 0) {
        utilSum += ((e.totalHours || 0) / fullDayHours) * 100;
        utilCount += 1;
      }
    }
    const avgUtil =
      utilCount > 0 ? Math.round((utilSum / utilCount) * 10) / 10 : 0;

    return {
      teamMembers,
      totalLogged: formatHours(totalLogged),
      pending: pendingCount,
      avgUtil,
    };
  }, [weekStats, fullDayHours]);

  const statCards = useMemo(
    () => [
      {
        key: "teamMembers",
        label: "Team Members",
        icon: Users,
        iconBg: "var(--color-primary-50)",
        iconColor: "var(--color-primary-700)",
      },
      {
        key: "totalLogged",
        label: "Total Logged",
        icon: CheckCheck,
        iconBg: "var(--color-success-light)",
        iconColor: "var(--color-success-dark)",
      },
      {
        key: "pending",
        label: "Pending Review",
        icon: Clock,
        iconBg: "var(--color-warning-light)",
        iconColor: "#B45309",
      },
      {
        key: "avgUtil",
        label: "Avg Utilization",
        icon: TrendingUp,
        iconBg: "var(--color-info-light)",
        iconColor: "var(--color-info)",
        suffix: "%",
      },
    ],
    [],
  );

  const weekStatsLoading =
    weekStatsFetching && weekStats.length === 0;

  /* =====================
   ACTIONS
===================== */

  const handleApprove = (timesheetId: string) => {
    actOnTimesheet({
      id: timesheetId,
      body: { decision: "approved" },
    });
  };

  const handleReject = (timesheetId: string) => {
    if (!canActOnApprovals) {
      toast.error("You do not have permission to reject timesheets.");
      return;
    }
    setSelectedTimesheet(timesheetId);
    setShowRejectModal(true);
  };

  const confirmReject = () => {
    if (!selectedTimesheet || !rejectionReason.trim()) return;
    actOnTimesheet({
      id: selectedTimesheet,
      body: { decision: "rejected", remarks: rejectionReason },
    });
    setShowRejectModal(false);
    setRejectionReason("");
    setSelectedTimesheet(null);
  };

  /* =====================
   ROW HELPERS
===================== */

  const formatEntryDate = (dateString: string) => {
    const key = toEntryDateKey(dateString);
    return key ? dayjs(key, "YYYY-MM-DD").format("DD MMM YYYY") : dateString;
  };

  const getUtilization = (record: TimesheetEntry) => {
    const util =
      fullDayHours > 0
        ? Math.round(((record.totalHours || 0) / fullDayHours) * 100)
        : 0;
    return { util, color: getUtilColor(util) };
  };

  /* =====================
   EXPORT
===================== */

  const handleExportXLSX = () => {
    try {
      const exportRows = weekStats.map((e) => {
        const { util } = getUtilization(e);
        const pill = getTimesheetStatusPill(e);
        return {
          Member: e.fullName || "",
          Email: e.workEmail || "",
          Date: formatEntryDate(e.date),
          Tasks: e.tasks.map((t) => t.name).join(", "),
          Logged: formatHours(e.totalHours || 0),
          Target: formatHours(fullDayHours),
          Utilization: `${util}%`,
          Status: pill.label,
        };
      });

      if (!exportRows.length) {
        toast.error("No timesheets to export for the selected week.");
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "Timesheet Management",
      );

      const filename = `Timesheet_Management_${weekLabel.replace(/\s/g, "_")}_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;

      XLSX.writeFile(workbook, filename);
      toast.success("Timesheets exported successfully");
    } catch (err) {
      console.error("Error exporting XLSX:", err);
      toast.error("Failed to export timesheets");
    }
  };

  /* =====================
   HEADER PIECES
===================== */

  const renderWeekSelect = () => (
    <div className="relative">
      <Calendar className="w-[15px] h-[15px] text-[var(--color-text-tertiary)] absolute left-[13px] top-1/2 -translate-y-1/2 pointer-events-none" />
      <select
        value={weekOffset}
        onChange={(e) => handleWeekChange(Number(e.target.value))}
        aria-label="Select week"
        className="h-10 pl-9 pr-8 rounded-[11px] border border-[var(--color-border-primary)] bg-[var(--color-bg-base)] text-[13px] font-semibold text-[var(--color-text-strong)] cursor-pointer appearance-none outline-none focus:border-primary-400"
      >
        {weekOptions.map((o) => (
          <option key={o.offset} value={o.offset}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown className="w-[15px] h-[15px] text-[var(--color-text-tertiary)] absolute right-[13px] top-1/2 -translate-y-1/2 pointer-events-none" />
    </div>
  );

  const renderExportButton = () => (
    <button
      onClick={handleExportXLSX}
      className="h-10 inline-flex items-center gap-2 px-4 rounded-[11px] bg-[var(--color-bg-base)] border border-[var(--color-border-primary)] text-[13px] font-semibold text-[var(--color-text-strong)] hover:border-[var(--color-border-input)] hover:translate-y-[-1px] transition-all"
    >
      <Download className="w-4 h-4" />
      Export
    </button>
  );

  const renderSearch = (
    value: string,
    onChange: (v: string) => void,
    placeholder: string,
  ) => (
    <div className="flex items-center gap-2 h-[38px] px-[13px] bg-[var(--color-bg-muted)] rounded-[11px] w-full max-w-[280px]">
      <Search className="w-4 h-4 text-[var(--color-text-tertiary)] flex-none" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-transparent outline-none text-[13px] text-[var(--color-text-primary)] w-full placeholder:text-[var(--color-text-placeholder)]"
      />
    </div>
  );

  const handleTabChange = (tab: "pending" | "history") => {
    setActiveTab(tab);
  };

  /* =====================
   RENDER
===================== */

  return (
    <div className="w-full">
      <PageHeader
        title="Timesheet Management"
        description="Review and approve your team's weekly timesheets"
        breadcrumbs={[{ label: "Home", path: "/dashboard" }, { label: "Timesheet Mgmt" }]}
        actions={
          <div className="flex flex-col items-end gap-2">
            {isAdmin && (
              <TabSelector
                size="sm"
                tabs={[
                  { k: VIEW_MODE.ALL, label: "All Employees" },
                  { k: VIEW_MODE.REPORTEES, label: "My Reportees" },
                ]}
                active={viewMode}
                onChange={handleViewModeChange}
              />
            )}
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
              <TabSelector
                size="md"
                tabs={[
                  { k: "pending", label: "Pending" },
                  { k: "history", label: "History" },
                ]}
                active={activeTab}
                onChange={handleTabChange}
              />
              {renderWeekSelect()}
              {renderExportButton()}
            </div>
          </div>
        }
      />

      {/* Summary Cards */}
      <div className="mb-5">
        <StatsCards
          variant="simple-icon"
          valueOnTop
          stats={weekSummary as unknown as Record<string, unknown>}
          cards={statCards}
          loading={weekStatsLoading}
          gridCols="lg:grid-cols-4"
        />
      </div>

      {/* Timesheets Table */}
      <div className="bg-[var(--color-bg-base)] rounded-[18px] border border-[var(--color-border-secondary)] shadow-soft overflow-hidden">
        <div className="w-full px-[22px] py-[18px] border-b border-[var(--color-border-tertiary)] flex items-center justify-between gap-3 flex-wrap">
          <span className="text-[16px] font-bold text-[var(--color-text-primary)]">
            {activeTab === "pending" ? (
              <>
                Team Timesheets{" "}
                <span className="text-[13px] font-semibold text-[var(--color-text-tertiary)]">
                  · {pendingTotal} pending
                </span>
              </>
            ) : (
              "Approval History"
            )}
          </span>
          {renderSearch(
            activeTab === "pending"
              ? pendingSearchInput
              : historySearchInput,
            (v) => {
              if (activeTab === "pending") {
                setPendingSearchInput(v);
                setPendingPage(1);
              } else {
                setHistorySearchInput(v);
                setHistoryPage(1);
              }
            },
            "Search team member…",
          )}
        </div>

        {activeTab === "pending" ? (
          <div className="px-0 py-0">
            <TeamTimesheetsTable
              rows={pendingTimesheets}
              loading={pendingLoading || pendingFetching || actionLoading}
              emptyMessage="No pending timesheets found"
              isPending
              canActOn={canActOnTimesheet}
              actionLoading={actionLoading}
              fullDayHours={fullDayHours}
              onApprove={handleApprove}
              onReject={handleReject}
              skeleton={
                <TimesheetManagementSkeleton
                  hasActions={canActOnApprovals}
                />
              }
              pagination={{
                page: pendingPage,
                pageSize: pendingLimit,
                total: pendingTotal,
                onPageChange: (p) => setPendingPage(p),
                onPageSizeChange: (l) => {
                  setPendingLimit(l);
                  setPendingPage(1);
                },
              }}
            />
          </div>
        ) : (
          <div className="px-0 py-0">
            <TeamTimesheetsTable
              rows={historyTimesheets}
              loading={historyLoading || historyFetching}
              emptyMessage="No historical timesheets found"
              isPending={false}
              canActOn={canActOnTimesheet}
              actionLoading={actionLoading}
              fullDayHours={fullDayHours}
              onApprove={handleApprove}
              onReject={handleReject}
              skeleton={
                <TimesheetManagementSkeleton hasActions={false} />
              }
              pagination={{
                page: historyPage,
                pageSize: historyLimit,
                total: historyTotal,
                onPageChange: (p) => setHistoryPage(p),
                onPageSizeChange: (l) => {
                  setHistoryLimit(l);
                  setHistoryPage(1);
                },
              }}
            />
          </div>
        )}
      </div>

      {/* Rejection Modal */}
      {showRejectModal && (
        <RejectionModal
          rejectionReason={rejectionReason}
          selectedLeave={selectedTimesheet}
          confirmReject={confirmReject}
          processing={actionLoading}
          setRejectionReason={setRejectionReason}
          setSelectedLeave={setSelectedTimesheet}
          setShowRejectModal={setShowRejectModal}
          title="Reject Timesheet"
          description="Please provide a reason for rejecting this timesheet entry."
        />
      )}
    </div>
  );
};

export default TimesheetManagement;
